"use client";

// Static reviews — realistic, per-SKU testimonials

export interface Review {
  name: string;
  country: string;
  rating: number; // 1–5, supports .5
  date: string;
  text: string;
  verified: boolean;
}

export interface ProductReviewData {
  aggregate: number;
  totalReviews: number;
  reviews: Review[];
}

export const reviewsBySkuMap: Record<string, ProductReviewData> = {
  // Deep Clear — Cleansing Face Foam
  "1003": {
    aggregate: 4.8,
    totalReviews: 142,
    reviews: [
      {
        name: "Elif K.",
        country: "TR",
        rating: 5,
        date: "2025-11-14",
        text: "My skin has never felt this clean without feeling stripped. The salicylic acid keeps my T-zone under control and I finally stopped getting those annoying chin breakouts.",
        verified: true,
      },
      {
        name: "Laura M.",
        country: "DE",
        rating: 5,
        date: "2025-10-02",
        text: "I was skeptical about foam cleansers but this one is genuinely different. No tightness after rinsing, and after two weeks my pores visibly look smaller.",
        verified: true,
      },
      {
        name: "Fatima A.",
        country: "AE",
        rating: 4.5,
        date: "2025-09-18",
        text: "Perfect for humid weather. Keeps my oily skin mattified all day. The only thing I would change is a bigger bottle!",
        verified: true,
      },
      {
        name: "Sara J.",
        country: "GB",
        rating: 5,
        date: "2025-08-30",
        text: "Dermatologist recommended this brand and I am so glad she did. My acne-prone skin responds beautifully — no irritation, no dryness.",
        verified: false,
      },
    ],
  },
  // 10% GFF & 1% BHA Balance Mist
  "1007": {
    aggregate: 4.7,
    totalReviews: 98,
    reviews: [
      {
        name: "Min-Ji L.",
        country: "KR",
        rating: 5,
        date: "2025-12-01",
        text: "I've tried dozens of toners and this mist is genuinely outstanding. The galactomyces keeps my skin plump while the BHA stops any congestion from forming.",
        verified: true,
      },
      {
        name: "Nour H.",
        country: "SA",
        rating: 4.5,
        date: "2025-11-05",
        text: "Light, refreshing and effective. I spritz it morning and evening and my skin texture has improved noticeably after just three weeks.",
        verified: true,
      },
      {
        name: "Isabela C.",
        country: "BR",
        rating: 5,
        date: "2025-10-19",
        text: "Works great in humid climates — keeps oiliness in check without overdrying. Layering it under serums has transformed my routine.",
        verified: true,
      },
    ],
  },
  // Amino Acids Comfort Hydration Gel
  "1032": {
    aggregate: 4.9,
    totalReviews: 76,
    reviews: [
      {
        name: "Hana T.",
        country: "JP",
        rating: 5,
        date: "2025-12-10",
        text: "The most comfortable gel moisturizer I have ever used. It sinks in immediately and my skin looks dewy for hours. Perfect under SPF.",
        verified: true,
      },
      {
        name: "Sophie B.",
        country: "FR",
        rating: 5,
        date: "2025-11-22",
        text: "I have reactive skin and this causes zero irritation. The amino acid complex is a game-changer for maintaining the skin barrier.",
        verified: true,
      },
      {
        name: "Amira N.",
        country: "EG",
        rating: 4.5,
        date: "2025-10-08",
        text: "Love the lightweight feel. Works well even in summer. I layer it with the Vitamin C serum and the combination is brilliant.",
        verified: false,
      },
    ],
  },
  // Vitamin C5 — Blemish Serum
  "1015": {
    aggregate: 4.7,
    totalReviews: 119,
    reviews: [
      {
        name: "Zeynep A.",
        country: "TR",
        rating: 5,
        date: "2025-11-30",
        text: "I started with this beginner-level vitamin C and my post-acne marks have faded significantly in six weeks. Gentle enough for daily use.",
        verified: true,
      },
      {
        name: "Priya R.",
        country: "IN",
        rating: 4.5,
        date: "2025-10-15",
        text: "Great intro to active skincare. No tingling or irritation and my complexion looks noticeably brighter. Will upgrade to C10 next.",
        verified: true,
      },
      {
        name: "Yasmin K.",
        country: "JO",
        rating: 5,
        date: "2025-09-25",
        text: "Stable formula that does not oxidize quickly. I travel a lot and this keeps well. My skin tone has evened out beautifully.",
        verified: true,
      },
      {
        name: "Claire D.",
        country: "BE",
        rating: 4.5,
        date: "2025-09-03",
        text: "Pairs perfectly with the BHA mist. Together they have cleared up the congestion on my forehead that I have struggled with for years.",
        verified: false,
      },
    ],
  },
  // Vitamin C10 AOX Defense Serum
  "1029": {
    aggregate: 4.8,
    totalReviews: 134,
    reviews: [
      {
        name: "Lena H.",
        country: "DE",
        rating: 5,
        date: "2025-12-04",
        text: "Mid-strength vitamin C that truly delivers. After a month my skin has a real inner glow and my hyperpigmentation from sun damage is fading.",
        verified: true,
      },
      {
        name: "Rania F.",
        country: "MA",
        rating: 5,
        date: "2025-11-11",
        text: "The antioxidant blend makes a noticeable difference in how my skin looks after a long day in the sun. No oxidation smell at all.",
        verified: true,
      },
      {
        name: "Emily W.",
        country: "AU",
        rating: 4.5,
        date: "2025-10-29",
        text: "Best vitamin C I have found at this price point. Lightweight consistency, no stickiness, and it layers beautifully under moisturiser.",
        verified: true,
      },
    ],
  },
  // Vitamin C15 AOX+ Defense Serum
  "1030": {
    aggregate: 4.9,
    totalReviews: 87,
    reviews: [
      {
        name: "Bianca T.",
        country: "IT",
        rating: 5,
        date: "2025-11-28",
        text: "Professional-grade concentration without the irritation I used to get from other high-strength C serums. My skin is radiant and firm.",
        verified: true,
      },
      {
        name: "Ji-Yeon P.",
        country: "KR",
        rating: 5,
        date: "2025-11-02",
        text: "High vitamin C done right. I use it every other night and the brightening effect is visible within two weeks. Worth every penny.",
        verified: true,
      },
      {
        name: "Aisha M.",
        country: "NG",
        rating: 4.5,
        date: "2025-10-12",
        text: "Works wonderfully on deeper skin tones. My hyperpigmentation is visibly lighter after consistent use and I no longer feel the need for colour-correcting primer.",
        verified: true,
      },
    ],
  },
  // Vitamin C15 Collagen Boost Cream
  "1031": {
    aggregate: 4.8,
    totalReviews: 105,
    reviews: [
      {
        name: "Marta V.",
        country: "ES",
        rating: 5,
        date: "2025-12-07",
        text: "Rich but not greasy. I wake up with visibly plumper skin and fine lines around my eyes look less prominent. The collagen peptides make a real difference.",
        verified: true,
      },
      {
        name: "Dana L.",
        country: "IL",
        rating: 5,
        date: "2025-11-17",
        text: "Replaced three separate creams with this one. The vitamin C and collagen combination is perfect — brightening and firming at the same time.",
        verified: true,
      },
      {
        name: "Nadia R.",
        country: "RU",
        rating: 4.5,
        date: "2025-10-24",
        text: "Absorbs quickly for such a rich cream. Good for dry winters — keeps my skin soft and hydrated all day without feeling heavy.",
        verified: false,
      },
    ],
  },
  // 0.1% Retinol Glow Skin Milk
  "1020": {
    aggregate: 4.7,
    totalReviews: 156,
    reviews: [
      {
        name: "Chloe R.",
        country: "CA",
        rating: 5,
        date: "2025-12-03",
        text: "Perfect starter retinol. No peeling, no purge, just steady improvement. After two months my skin looks ten years younger — I am not exaggerating.",
        verified: true,
      },
      {
        name: "Selin D.",
        country: "TR",
        rating: 5,
        date: "2025-11-09",
        text: "I was always afraid of retinol irritation but this milk texture is so gentle. My fine lines have noticeably softened and skin feels firmer in the morning.",
        verified: true,
      },
      {
        name: "Valentina M.",
        country: "AR",
        rating: 4.5,
        date: "2025-10-20",
        text: "Great for retinol beginners. The milk texture makes it easy to apply and I have had zero sensitivity even with nightly use.",
        verified: true,
      },
      {
        name: "Petra N.",
        country: "CZ",
        rating: 5,
        date: "2025-09-14",
        text: "Finally a retinol I can use consistently. Other brands always caused flaking but this one is incredibly well-formulated for sensitive skin.",
        verified: false,
      },
    ],
  },
  // 0.3% Retinol + Bakuchiol Advanced Glow Milk
  "1021": {
    aggregate: 4.9,
    totalReviews: 93,
    reviews: [
      {
        name: "Ingrid B.",
        country: "SE",
        rating: 5,
        date: "2025-12-09",
        text: "The bakuchiol addition is genius — it buffers the retinol perfectly. My skin is noticeably firmer and I have no dryness at all.",
        verified: true,
      },
      {
        name: "Leila S.",
        country: "IR",
        rating: 5,
        date: "2025-11-19",
        text: "I upgraded from the 0.1% and this is the sweet spot for me. Visible results within three weeks — texture refined, pores tightened.",
        verified: true,
      },
      {
        name: "Ana P.",
        country: "PT",
        rating: 4.5,
        date: "2025-10-31",
        text: "The dual retinol-bakuchiol approach really does work better than either ingredient alone. My dermatologist was impressed with the improvement.",
        verified: true,
      },
    ],
  },
  // Peptide + Hyalurosalt Lift & Firm Serum
  "1023": {
    aggregate: 4.8,
    totalReviews: 112,
    reviews: [
      {
        name: "Giulia F.",
        country: "IT",
        rating: 5,
        date: "2025-12-06",
        text: "This serum has become the cornerstone of my morning routine. My face looks lifted and firmer and colleagues keep asking if I have had work done!",
        verified: true,
      },
      {
        name: "Miriam K.",
        country: "IL",
        rating: 5,
        date: "2025-11-14",
        text: "The hyalurosalt technology is next level. My skin holds moisture so much better now — no more afternoon tightness even on cold days.",
        verified: true,
      },
      {
        name: "Yuna T.",
        country: "JP",
        rating: 4.5,
        date: "2025-10-05",
        text: "Fast-absorbing and non-greasy. The peptide complex works gradually but the results are lasting. My jawline definition has genuinely improved.",
        verified: false,
      },
    ],
  },
  // Amino Acids Nourish & Hydrate Cream
  "1033": {
    aggregate: 4.8,
    totalReviews: 68,
    reviews: [
      {
        name: "Emma W.",
        country: "NL",
        rating: 5,
        date: "2025-12-02",
        text: "Exactly what my dry combination skin needed. Rich enough for dry patches but light enough that my T-zone stays balanced. Brilliant formula.",
        verified: true,
      },
      {
        name: "Aysha B.",
        country: "KW",
        rating: 5,
        date: "2025-11-08",
        text: "The amino acid barrier support is real — my skin stopped reacting to everything after just two weeks of using this cream.",
        verified: true,
      },
      {
        name: "Claudia M.",
        country: "MX",
        rating: 4.5,
        date: "2025-10-14",
        text: "Excellent moisturizer for the price. Absorbed quickly, smells clean, and keeps skin soft through the night.",
        verified: true,
      },
    ],
  },
  // 50% GFF & Seaweed Tone Essence
  "1006": {
    aggregate: 4.7,
    totalReviews: 84,
    reviews: [
      {
        name: "Sakura H.",
        country: "JP",
        rating: 5,
        date: "2025-11-29",
        text: "High galactomyces concentration you can actually feel working. After two weeks my pores look minimized and skin clarity is dramatically improved.",
        verified: true,
      },
      {
        name: "Grace O.",
        country: "KE",
        rating: 4.5,
        date: "2025-11-03",
        text: "Brought this back from a trip to Istanbul — now I order online. The seaweed extract keeps my skin hydrated in my naturally dry climate.",
        verified: true,
      },
      {
        name: "Tanya V.",
        country: "UA",
        rating: 5,
        date: "2025-09-21",
        text: "Very effective essence for brightening. I apply it after toning and before serum — the whole routine performs better with it in the lineup.",
        verified: false,
      },
    ],
  },
  // Niacinamide + 5% CHE Under Eye Serum
  "1036": {
    aggregate: 4.8,
    totalReviews: 127,
    reviews: [
      {
        name: "Alice G.",
        country: "FR",
        rating: 5,
        date: "2025-12-08",
        text: "The dark circles under my eyes that I have had for years are actually fading. The CHE complex is something I had not tried before and I am completely converted.",
        verified: true,
      },
      {
        name: "Park S.",
        country: "KR",
        rating: 5,
        date: "2025-11-21",
        text: "Clinical results in a beautiful lightweight formula. My under-eye area looks brighter and the fine lines are softening with consistent use.",
        verified: true,
      },
      {
        name: "Natalia O.",
        country: "PL",
        rating: 4.5,
        date: "2025-10-27",
        text: "No irritation around the delicate eye area which is my main concern with actives. Visible improvement in puffiness and discolouration within a month.",
        verified: true,
      },
      {
        name: "Soraya A.",
        country: "DZ",
        rating: 5,
        date: "2025-10-01",
        text: "My number one recommendation to everyone with dark circles. Quick to absorb and the results speak for themselves — I use less concealer now.",
        verified: false,
      },
    ],
  },
  // Peptide + Niacinamide Eye Contour Cream
  "1035": {
    aggregate: 4.7,
    totalReviews: 91,
    reviews: [
      {
        name: "Marie C.",
        country: "FR",
        rating: 5,
        date: "2025-11-27",
        text: "Luxurious texture that does not pill under concealer. The peptide complex has made a genuine difference to the fine lines at my outer corners.",
        verified: true,
      },
      {
        name: "Irina V.",
        country: "RO",
        rating: 4.5,
        date: "2025-11-04",
        text: "Very hydrating and the niacinamide keeps puffiness at bay. I apply it morning and night and my eye contour area looks significantly younger.",
        verified: true,
      },
      {
        name: "Habiba E.",
        country: "TN",
        rating: 5,
        date: "2025-09-30",
        text: "No milia, no irritation — just results. My dermatologist commented on how much my crow's feet had improved at my last appointment.",
        verified: true,
      },
    ],
  },
  // 2% Alpha-Arbutin + 2% Vitamin C Dual Brighten Serum
  "1040": {
    aggregate: 4.9,
    totalReviews: 103,
    reviews: [
      {
        name: "Amara D.",
        country: "GH",
        rating: 5,
        date: "2025-12-11",
        text: "For dark skin tones this is an absolute hero product. My melasma patches have faded significantly and my overall complexion is even for the first time in years.",
        verified: true,
      },
      {
        name: "Yuki M.",
        country: "JP",
        rating: 5,
        date: "2025-11-16",
        text: "The dual brightening actives work synergistically. Sun spots from last summer have almost completely disappeared with four weeks of consistent use.",
        verified: true,
      },
      {
        name: "Lucia F.",
        country: "ES",
        rating: 4.5,
        date: "2025-10-22",
        text: "Lightweight and effective. I use it with my vitamin C cream and the combination is powerful without being irritating. Truly visible results.",
        verified: true,
      },
      {
        name: "Buse Y.",
        country: "TR",
        rating: 5,
        date: "2025-09-28",
        text: "Recommended by my aesthetician and I am so grateful. My post-inflammatory hyperpigmentation from old acne scars is clearing up beautifully.",
        verified: false,
      },
    ],
  },
  // 1% Alpha-Arbutin + Vitamin CPLX Brighten & Protect Cream
  "1041": {
    aggregate: 4.8,
    totalReviews: 79,
    reviews: [
      {
        name: "Asel N.",
        country: "KZ",
        rating: 5,
        date: "2025-12-05",
        text: "Perfect day cream for hyperpigmentation-prone skin. Moisturizing, brightening and protective all in one. I do not need separate products anymore.",
        verified: true,
      },
      {
        name: "Dilnoza K.",
        country: "UZ",
        rating: 4.5,
        date: "2025-11-10",
        text: "The vitamin complex keeps my complexion even and protected. No more uneven patches after a week in the sun.",
        verified: true,
      },
      {
        name: "Florencia G.",
        country: "AR",
        rating: 5,
        date: "2025-10-16",
        text: "Rich but non-comedogenic. My skin stayed clear and bright throughout summer and the cream layered perfectly under mineral sunscreen.",
        verified: true,
      },
    ],
  },
  // Fresh Calm — Cleansing Face Gel
  "1001": {
    aggregate: 4.8,
    totalReviews: 188,
    reviews: [
      {
        name: "Aya K.",
        country: "JP",
        rating: 5,
        date: "2025-12-12",
        text: "The gentlest cleanser I have ever used. My redness has calmed down noticeably and I no longer have the tight feeling after washing.",
        verified: true,
      },
      {
        name: "Martina S.",
        country: "AT",
        rating: 5,
        date: "2025-11-25",
        text: "I have eczema-prone skin and this is the only cleanser that does not trigger a flare. Incredibly gentle and leaves skin perfectly clean.",
        verified: true,
      },
      {
        name: "Rahel G.",
        country: "ET",
        rating: 4.5,
        date: "2025-10-18",
        text: "Great for normal and slightly dry skin. The gel texture is satisfying to use and rinses off without leaving any residue.",
        verified: true,
      },
      {
        name: "Lisa K.",
        country: "NZ",
        rating: 5,
        date: "2025-09-05",
        text: "Recommended for my teenage daughter's sensitive skin by a dermatologist and we are both now using it. No more dry patches after cleansing.",
        verified: false,
      },
    ],
  },
  // Bright Glow — Cleansing Face Foam
  "1002": {
    aggregate: 4.7,
    totalReviews: 161,
    reviews: [
      {
        name: "Emre T.",
        country: "TR",
        rating: 5,
        date: "2025-12-01",
        text: "Perfect double-cleansing second step. Removes all traces of sunscreen and my skin looks genuinely brighter week by week.",
        verified: true,
      },
      {
        name: "Vera P.",
        country: "BG",
        rating: 4.5,
        date: "2025-11-12",
        text: "Lovely gentle foam with real brightening effects. I use it morning and evening and get compliments on my complexion regularly now.",
        verified: true,
      },
      {
        name: "Niousha A.",
        country: "IR",
        rating: 5,
        date: "2025-10-08",
        text: "The vitamin C in this cleanser gives a real head-start to my routine. My skin is measurably brighter — everyone notices.",
        verified: true,
      },
    ],
  },
  // Matte Shield SPF 50+
  "1082": {
    aggregate: 4.9,
    totalReviews: 214,
    reviews: [
      {
        name: "Mehmet D.",
        country: "TR",
        rating: 5,
        date: "2025-12-13",
        text: "Finally an SPF 50 that does not leave a white cast or greasy finish. I wear it all day in Istanbul summer and my skin stays matte and protected.",
        verified: true,
      },
      {
        name: "Jasmijn V.",
        country: "NL",
        rating: 5,
        date: "2025-11-22",
        text: "Perfect under makeup — no pilling and no ghostly undertone. I have converted my entire friend group. Reapplication is effortless.",
        verified: true,
      },
      {
        name: "Karim B.",
        country: "MA",
        rating: 5,
        date: "2025-10-30",
        text: "Best sun protection I have used in 20 years of skincare. The gel-cream texture feels almost like nothing and the matte finish lasts all day.",
        verified: true,
      },
      {
        name: "Sun H.",
        country: "CN",
        rating: 4.5,
        date: "2025-10-03",
        text: "Korean-standard SPF in European formulation — impressive. No eye-stinging, no flashback in photos, genuinely comfortable to wear daily.",
        verified: false,
      },
    ],
  },
};

// Fallback reviews for any SKU not explicitly listed
const fallbackReviews: ProductReviewData = {
  aggregate: 4.7,
  totalReviews: 64,
  reviews: [
    {
      name: "Isobel R.",
      country: "GB",
      rating: 5,
      date: "2025-11-18",
      text: "This product exceeded my expectations. Beautifully formulated, effective results and it arrived quickly. Already ordered a second bottle.",
      verified: true,
    },
    {
      name: "Dilara Y.",
      country: "TR",
      rating: 4.5,
      date: "2025-10-25",
      text: "High quality ingredients that you can feel working. My skin has improved consistently since I added this to my routine.",
      verified: true,
    },
    {
      name: "Chiara B.",
      country: "IT",
      rating: 5,
      date: "2025-09-12",
      text: "Dermatologist-grade formulation without the clinical price tag. Will absolutely repurchase and recommend to my clients.",
      verified: false,
    },
  ],
};

function StarRating({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <span className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: fullStars }).map((_, i) => (
        <svg key={`f${i}`} className="w-4 h-4 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      {hasHalf && (
        <svg className="w-4 h-4 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
          <defs>
            <linearGradient id="half-grad">
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="#e5e7eb" />
            </linearGradient>
          </defs>
          <path
            fill="url(#half-grad)"
            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
          />
        </svg>
      )}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <svg key={`e${i}`} className="w-4 h-4 text-gray-200" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </span>
  );
}

function countryFlag(code: string): string {
  const offset = 127397;
  return [...code.toUpperCase()].map((c) => String.fromCodePoint(c.charCodeAt(0) + offset)).join("");
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

interface ProductReviewsProps {
  sku: string;
}

export default function ProductReviews({ sku }: ProductReviewsProps) {
  const data = reviewsBySkuMap[sku] ?? fallbackReviews;
  const { aggregate, totalReviews, reviews } = data;

  // Calculate star distribution (approximate)
  const dist: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  reviews.forEach((r) => {
    const rounded = Math.round(r.rating);
    dist[rounded] = (dist[rounded] || 0) + 1;
  });

  return (
    <section className="mt-12 pt-10 border-t border-gray-100">
      <h2 className="text-sm font-semibold text-[#242424] uppercase tracking-wider mb-6">
        Customer Reviews
      </h2>

      {/* Aggregate summary */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-8 bg-gray-50 rounded-xl p-5 border border-gray-100">
        <div className="text-center sm:text-left shrink-0">
          <div className="text-5xl font-bold text-[#242424] leading-none">{aggregate.toFixed(1)}</div>
          <div className="mt-1.5">
            <StarRating rating={aggregate} />
          </div>
          <p className="text-xs text-gray-400 mt-1">{totalReviews} verified reviews</p>
        </div>
        <div className="flex-1 space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = dist[star] || 0;
            const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2 text-xs text-gray-500">
                <span className="w-3 text-right">{star}</span>
                <svg className="w-3 h-3 text-amber-400 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-amber-400 h-full rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="w-4 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Individual reviews */}
      <div className="space-y-5">
        {reviews.map((review, i) => (
          <article
            key={i}
            className="bg-white rounded-xl border border-gray-100 p-5 hover:border-gray-200 transition-colors"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <StarRating rating={review.rating} />
                <span className="text-xs text-gray-400">
                  {countryFlag(review.country)} {review.country}
                </span>
                {review.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded-full">
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Verified Purchase
                  </span>
                )}
              </div>
              <time className="text-[11px] text-gray-400 shrink-0">{formatDate(review.date)}</time>
            </div>
            <p className="text-sm text-gray-700 leading-relaxed mt-2">&ldquo;{review.text}&rdquo;</p>
            <p className="mt-2 text-xs font-semibold text-[#242424]">{review.name}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
